import withObservables from "@nozbe/with-observables";
import { Fragment, useContext, useState } from "react";
import { useSnackbar } from "../../../context/SnackbarContext";
import { Box, capitalize, CircularProgress } from "@material-ui/core";
import {
  BlackCheckIcon,
  DeleteNewIcon,
  DownloadIcon,
  PrintIcon,
} from "../../../assets/icons/icon";
import { ParagraphText } from "../../../Libraries";
import { LanguageContext } from "../../../context/LanguageContext";
import { InvListTableHeads } from "./SalePOSList";
import { PopoverMenu, ReceiptPage } from "../../common";
import { catchError, of } from "rxjs";
import {
  bulkDeleteOrders,
  bulkRestoreStock,
} from "../../../database/helpers/orders";
import { bulkDeleteInventory } from "../../../database/helpers/inventory";
import {
  bulkCreatePayments,
  bulkDeletePaymentsV2,
  getPaymentsByInvoiceExternal,
} from "../../../database/helpers/payment";
import { Sync } from "../../../SyncService";
import { useAuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";

import { analytics, triggerLogEvent } from "../../../config/firebase";
import ConfirmationModal from "../../common/Modals/ConfirmationModal";
import RecordPaymentModal from "../../common/Modals/RecordPaymentModal";
import {
  calculateTotalOutstanding,
  findCustomerByID,
} from "../../../database/helpers/customer";
import { uuid } from "../../../oscar-pos-core/constants";
import { handleDownloadInvoice } from "../NewInvoiceBook/invoiceBookUtils";
import SidebarBodyContent from "../../common/UBRightSidebar/SidebarBodyContent";
import { formattedDateWithSpaceAndTime } from "../../../utils";
import AddContactSelector from "../../common/AddContactSelector";
import { sentenceCase } from "../../../constants";
import AttachContactMessage from "../../common/AttachContactMessage";

const InvoiceRowItem = ({
  customerDetail,
  detail,
  transaction,
  type,
  orders,
  refetchList,
  index,
  selectedContactId,
  setSelectedContactId,
  justClosedDropdownRef,
  customers,
}) => {
  const [I18n] = useContext(LanguageContext);
  const { id, customer, ...transactionDisplayData } = detail || {};
  transactionDisplayData.cust_name = customerDetail?.name || "-";
  const [isLoading, setIsLoading] = useState(false);
  const [openDetailSidebar, setOpenDetailSidebar] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoadingRecordPayment, setIsLoadingRecordPayment] = useState(false);
  const [isOpenRecordPaymentModal, setIsOpenRecordPaymentModal] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");

  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { business, user } = useAuthContext();

  const contactList = customers?.length ? customers : [];

  const handleDetail = () => {
    triggerLogEvent(analytics, "pos_detail");
    setOpenDetailSidebar(!openDetailSidebar);
  };

  const handleDownload = () => {
    setIsLoading(true);
    try {
      handleDownloadInvoice(
        transaction,
        business,
        (error) => {

          if (error) {
            if (!user?.phone_number) {
              showSnackbar({
                message: I18n.report_download_is_not_available_yet,
                severity: "error",
              });
            } else {
              showSnackbar({
                message: I18n.error_downloading_report,
                severity: "error",
              });
            }
            setIsLoading(false);
            return;
          }

          setIsLoading(false);
          showSnackbar({
            message: I18n.successfully_download_invoice,
            severity: "success",
          });
        },
        navigate,
        "Rupin Sale Invoice",
      );
      triggerLogEvent(analytics, "pos_option_download");
    } catch (err) {
      setIsLoading(false);
    }
  };

  const InvoiceActions = [
    {
      label: (
        <ReceiptPage
          isDownloadBtn={false}
          invoiceId={transaction?.id ?? ""}
          isPrintIcon={false}
          isPrintBtn={true}
          handlePrintLogEvent={() =>
            triggerLogEvent(analytics, "pos_options_print")
          }
          customPrintBtn={
            <Box display={"flex"} alignItems={"center"}>
              <Box marginRight={2} display={"flex"}>
                <PrintIcon width={20} height={20} />
              </Box>
              <ParagraphText className="textCapitalizeFirstLetter">
                {I18n.print}
              </ParagraphText>
            </Box>
          }
        />
      ),
      action: (e) => {
        e.stopPropagation();
      },
    },
    {
      label: (
        <Fragment>
          <DownloadIcon width={20} height={18} id="download_black_btn" />
          <ParagraphText className="textCapitalizeFirstLetter">
            {I18n.download_invoice}
          </ParagraphText>
        </Fragment>
      ),
      action: (e) => {
        e.stopPropagation();
        triggerLogEvent(analytics, "sale_option_download");
        handleDownload();
      },
    },
    {
      label: (
        <Box display={"flex"} alignItems={"center"}>
          <Box marginRight={2} display={"flex"}>
            <BlackCheckIcon width={16} height={16} id="record_payment_btn" />
          </Box>
          <ParagraphText className="textCapitalizeFirstLetter">
            {I18n.record_payment}
          </ParagraphText>
        </Box>
      ),
      action: (e) => {
        e.stopPropagation();
        triggerLogEvent(analytics, "sb_option_mark_paid");
        handleOpenRecordPaymentModal();
      },
      hideOnPaid: true,
    },
    {
      label: (
        <Box display={"flex"} alignItems={"center"}>
          <Box marginRight={2} display={"flex"}>
            <DeleteNewIcon width={20} height={18} id="download_black_btn" />
          </Box>
          <ParagraphText className="textCapitalizeFirstLetter">
            {I18n.delete}
          </ParagraphText>
        </Box>
      ),
      action: (e) => {
        e.stopPropagation();
        setShowDeleteModal(true);
        triggerLogEvent(analytics, "pos_options_delete");
      },
    },
  ];

  const handleOpenRecordPaymentModal = () => {
    setIsOpenRecordPaymentModal(true);
  };

  const handleDelete = async () => {
    setShowDeleteModal(false);
    if (customerDetail) {
      let payments = await transaction.payment.fetch();
      let initialOutstanding = customerDetail.total_outstanding_payment;
      await bulkDeletePaymentsV2(payments);
      await customerDetail.updateTotalOutstanding(
        initialOutstanding - transaction.amount,
      );
    }
    if (orders.length) {
      let toDelete = [];
      await Promise.all(
        orders.map(async (item) => {
          if (!item.is_open_item) {
            let stock = await item.stock.fetch();
            toDelete.push({
              original: item,
              updated: stock,
            });
          }
        }),
      );
      if (toDelete.length) {
        await bulkRestoreStock(toDelete, "in");
        await bulkDeleteOrders(toDelete);
        await bulkDeleteInventory(transaction, toDelete, "in", "pos");
      }
    }
    await transaction.markAsDeleted();
    refetchList();
    setIsLoading(false);
    Sync(user.phone_number, navigate);
    showSnackbar({
      message: I18n.transaction_deleted,
      severity: "success",
      icon: "",
    });
  };

  const handleRecordPayment = (
    selectedMethod,
    selectedDate,
    receivedAmount,
  ) => {
    handleEditInvoice(
      Number(receivedAmount) + Number(transaction.advance_amount),
      selectedMethod,
      selectedDate,
      customerDetail,
    );
  };

  const handleEditInvoice = async (
    amountRecvd,
    paymentMethod,
    transaction_date,
  ) => {
    setIsLoadingRecordPayment(true);
    triggerLogEvent(analytics, "sale_record");

    const invoiceAmountObj = {
      discount: transaction?.discount || 0,
      discount_unit: transaction?.discount_unit || "rs",
      tax_rate: transaction?.tax_rate || 0,
      total: 0,
      sub_total: transaction?.sub_total || 0,
      advance_amount: transaction?.advance_amount || 0,
      profit: transaction?.profit || 0,
    };
    let invoiceAmount = Number(transaction?.amount) || 0;
    let invoiceStatus =
      invoiceAmount - (Number(amountRecvd) || 0) === 0 ? "paid" : "unpaid";
    let partialAmount = invoiceAmount - (Number(amountRecvd) || 0);
    const currentMethod = !paymentMethod ? transaction?.method : paymentMethod;

    const fetchPaidTo = transaction?.customer?.id
      ? await findCustomerByID(transaction?.customer?.id)
      : null;

    const dataToEdit = {
      customer: transaction?.customer?.id ? fetchPaidTo : null,
      recording_uri: transaction?.recording_uri,
      advance_amount: Number(amountRecvd) || 0,
      invoice_number:
        transaction?.invoice_number || business.invoice_number_count,
      invoice_prefix:
        transaction?.invoice_prefix || business.invoice_number_prefix,
      invoice_title: transaction?.invoice_title,
      discount_unit: transaction?.discount_unit,
      sales_amount: transaction?.sales_amount,
      document_url: transaction?.document_url,
      cost_amount: transaction?.cost_amount,
      attachment: transaction?.attachment,
      sub_total: transaction?.sub_total,
      signature: transaction?.signature,
      tax_title: transaction?.tax_title,
      tranType: transaction?.tranType,
      image_url: transaction?.image || null,
      date: transaction_date
        ? transaction_date
        : transaction.transacted_at || new Date(),
      category: transaction?.category,
      discount: transaction?.discount,
      tax_rate: transaction?.tax_rate,
      amount: invoiceAmount,
      method: currentMethod,
      wallet: currentMethod === "wallet" ? paymentMethod?.name : null,
      profit: transaction?.profit,
      status: invoiceStatus,
      total: Number(invoiceAmountObj?.total) || 0,
      note: transaction?.note || "",
      bank: currentMethod === "bank" ? paymentMethod?.name : null,
      partialAmount,
    };
    if (dataToEdit.customer) {
      let dataForPayment = {
        udhaar_note: dataToEdit?.note,
        date: dataToEdit?.date || new Date(),
        image_url: dataToEdit?.image_url || null,
        invoiceDetail: transaction,
        type: "cashbook",
      };
      // if total amount is changed wrt to current invoice, we will create or update invoice full payment entry
      if (invoiceAmount != transaction?.amount) {
        dataForPayment["amount"] = invoiceAmount;
        let invoicePayment = await getPaymentsByInvoiceExternal(
          transaction?.id,
          "full_invoice",
        );

        if (invoicePayment.length) {
          await invoicePayment[0]?.updatePayment(dataForPayment);
        } else {
          await dataToEdit.customer.createPayment({
            ...dataForPayment,
            uid: uuid(),
            date: new Date(),
            payment_mode: "Jama",
            external_type: "full_invoice",
          });
        }
      }
      if (
        Number(transaction?.advance_amount) !=
        Number(dataToEdit?.advance_amount)
      ) {
        // if received or partial amount is changed wrt to current invoice, we will create or update invoice partial payment entry

        dataForPayment["amount"] = Number(dataToEdit?.advance_amount);

        let invoicePayment = await getPaymentsByInvoiceExternal(
          transaction?.id,
          "partial_invoice",
        );

        if (invoicePayment.length) {
          await invoicePayment[0]?.updatePayment(dataForPayment);
        } else {
          let payObj = {
            date: dataToEdit?.date || new Date(),
            note: dataToEdit?.note || "",
            image_url: dataToEdit?.image_url || "",
            type: "cashbook",
            uid: uuid(),
            amount: Number(dataToEdit.advance_amount),
            payment_mode: "Jama",
            external_type: "partial_invoice",
          };

          await bulkCreatePayments(business, dataToEdit.customer, transaction, [
            payObj,
          ]);
        }
      }

      let totalOutstanding = await calculateTotalOutstanding(
        dataToEdit.customer,
      );

      await dataToEdit.customer.updateTotalOutstanding(totalOutstanding);
    }
    await transaction.updateInvocie(dataToEdit);
    if (fetchPaidTo) triggerLogEvent(analytics, "sale_record_name");
    if (dataToEdit.advance_amount)
      triggerLogEvent(analytics, "sale_record_amount");
    if (dataToEdit.method) triggerLogEvent(analytics, "sale_record_method");
    setIsLoadingRecordPayment(false);
    setIsOpenRecordPaymentModal(false);
    setOpenDetailSidebar(false);
    refetchList();
    Sync(user?.phone_number, navigate);
    showSnackbar({
      message: I18n.update_record_sucessfully,
      severity: "success",
    });
  };

  const customerInfoData = [
    {
      title: I18n.date,
      value: formattedDateWithSpaceAndTime(transaction?.created_at),
    },
    {
      title: I18n.contact,
      value: customerDetail?.name || "-",
    },
    {
      title: I18n.payment_method,
      // method is stored in English (Cash/Bank/Udhaar...); show the translated label when one exists
      value: transaction?.method
        ? sentenceCase(I18n[String(transaction.method).toLowerCase()] || transaction.method)
        : "-",
    },
    {
      title: I18n.notes,
      value: transaction?.note || "-",
    },
  ];

  const handleCreateCustomerPayment = async (customer, data, invoice) => {
    let payObj = {
      date: data?.date || new Date(),
      udhaar_note: data?.note || "",
      imageURL: data?.image_url || "",
      type: "invoicebook",
    };

    let paymentArr = [
      {
        ...payObj,
        uid: uuid(),
        amount: data.amount,
        payment_mode: type == "in" ? "Udhaar" : "Jama",
        external_type: "full_invoice",
      },
    ];

    if (data?.advance_amount > 0) {
      paymentArr.unshift({
        ...payObj,
        uid: uuid(),
        amount: Number(data.advance_amount),
        payment_mode: type == "in" ? "Jama" : "Udhaar",
        external_type: "partial_invoice",
      });
    }

    await bulkCreatePayments(business, customer, invoice, paymentArr);

    let totalOutstanding = await calculateTotalOutstanding(customer);

    await customer.updateTotalOutstanding(totalOutstanding);
  };

  const handleAddCustomer = async (customer) => {
    try {
      await transaction.updateInvoiceCustomer(customer);

      if (transaction?.status?.toLowerCase() != "paid") {
        const data = {
          amount: transaction.amount,
          date: transaction.transacted_at,
          image_url: transaction.image_url,
          note: transaction.note,
          advance_amount: transaction.advance_amount,
        };

        await handleCreateCustomerPayment(customer, data, transaction);
      }
      setSearchQuery("");
      Sync(user.phone_number, navigate);
      showSnackbar({
        message: I18n.contact_attached_successfully,
        severity: "success",
      });
      setSelectedCustomer("");
    } catch (error) {
      await transaction.updateInvoiceCustomer(null);
    }
  };

  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setSelectedContactId(null);
  };

  return (
    <Box
      className="invoiceListTableBody AccountItemListWrapper user-select-none"
      id={`salePos_list_${index}`}
      onClick={(e) => {
        if (
          showDeleteModal ||
          selectedCustomer ||
          openDetailSidebar ||
          isOpenRecordPaymentModal ||
          justClosedDropdownRef.current
        )
          return;
        e.stopPropagation();
        if (selectedContactId) {
          setSelectedContactId("");
          return;
        }
        handleDetail();
      }}
    >
      {InvListTableHeads.map((headInfo, ind) => (
        <Box
          key={ind}
          className={`invListTableHeaderCol ${headInfo?.className}`}
        >
          {(!("displayOn" in headInfo) || headInfo.displayOn == type) &&
            (headInfo?.showDataKey == "cust_name" ? (
              <AddContactSelector
                key={headInfo.name}
                selectedContact={
                  customerDetail?.name
                    ? {
                        id: customerDetail?.id,
                        name: customerDetail.name,
                      }
                    : null
                }
                contacts={contactList || []}
                isOpen={selectedContactId === transaction?.id}
                onOpenChange={(open) => {
                  if (!customerDetail && !selectedContactId) {
                    setSelectedContactId(open ? transaction.id : "");
                  } else if (selectedContactId) {
                    justClosedDropdownRef.current = true;
                    setSelectedContactId("");
                    setTimeout(() => {
                      justClosedDropdownRef.current = false;
                    }, 100);
                  } else if (
                    !selectedContactId &&
                    !justClosedDropdownRef?.current
                  ) {
                    setOpenDetailSidebar(true);
                  }
                }}
                onSelect={handleSelectCustomer}
                addContactLabel={I18n.add_contact || "Add contact"}
                searchPlaceholder={I18n.search_contact || "Search contact"}
              />
            ) : (
              <Box key={headInfo.name}>
                <ParagraphText
                  className={`fontSize14 fontWeight400 displayFlex ${
                    transaction?.status?.toLowerCase() != "paid" &&
                    headInfo.showDataKey == "amount"
                      ? "foundationOrangeColor"
                      : headInfo?.bodyClassName || ""
                  }`}
                  onClick={(e) => {
                    if (
                      showDeleteModal ||
                      selectedCustomer ||
                      openDetailSidebar ||
                      isOpenRecordPaymentModal ||
                      justClosedDropdownRef.current
                    )
                      return;
                    e.stopPropagation();
                    if (selectedContactId) {
                      setSelectedContactId("");
                      return;
                    }
                    handleDetail();
                  }}
                >
                  {!!transactionDisplayData[headInfo.showDataKey] && (
                    <span
                      className="truncate"
                      style={
                        headInfo.showDataKey == "invoice_number"
                          ? { width: "87px" }
                          : {}
                      }
                    >
                      {" "}
                      {transactionDisplayData[headInfo.showDataKey]}
                    </span>
                  )}
                  {headInfo?.showStatus && transactionDisplayData["status"] ? (
                    <span>
                      {capitalize(
                        transactionDisplayData["status"]?.toString?.(),
                      )}
                    </span>
                  ) : null}
                  {headInfo?.showActionButton ? (
                    <div className="tblOptnBox" role="button">
                      <PopoverMenu
                        id="sale_pos"
                        menuOptions={InvoiceActions.filter(
                          (acts) =>
                            !(
                              acts?.hideOnPaid &&
                              transaction?.status?.toLowerCase() === "paid"
                            ),
                        )}
                        popOverStyle={{
                          marginLeft: "5px",
                          borderRadius: "16px !important",
                        }}
                        buttonStyle={{
                          backgroundColor: "var(--foundationGrey)",
                          width: "32px",
                          height: "36px",
                          borderRadius: "50%",
                        }}
                        popoverButton={
                          isLoading && <CircularProgress size={"15px"} />
                        }
                        disabled={isLoading}
                      />
                    </div>
                  ) : null}
                </ParagraphText>
              </Box>
            ))}
        </Box>
      ))}

      {openDetailSidebar ? (
        <SidebarBodyContent
          onClose={() => setOpenDetailSidebar(false)}
          open={openDetailSidebar}
          quickSale={transaction}
          refetchList={refetchList}
          business={business}
          handleDelete={() => setShowDeleteModal(true)}
          handleMarkAsPaid={() => {
            triggerLogEvent(analytics, "pos_side_bar_record_payment");
            handleOpenRecordPaymentModal();
          }}
          isShowDeleteBtn={false}
          isShowEditBtn={false}
          isShowAttachment={false}
          customerInfoData={customerInfoData}
        />
      ) : null}

      {showDeleteModal && (
        <ConfirmationModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title={I18n.this_action_cannot_be_undone}
          message={I18n.are_you_sure_you_want_to_delete_transaction}
          confirmText={I18n.delete}
          id="sale_pos"
        />
      )}

      {selectedCustomer && (
        <ConfirmationModal
          id="sale_add_contact_cofirm_modal"
          open={Boolean(selectedCustomer)}
          onClose={() => setSelectedCustomer("")}
          onConfirm={() => handleAddCustomer(selectedCustomer)}
          title={I18n.you_wont_be_able_to_change_it_later}
          confirmButtonClassName="ubPrimaryButton"
          htmlMessage={
            <AttachContactMessage name={selectedCustomer.name} />
          }
          confirmText={I18n.confirm}
        />
      )}

      {isOpenRecordPaymentModal ? (
        <Box onClick={(e) => e.stopPropagation()}>
          <RecordPaymentModal
            handleConfirm={handleRecordPayment}
            handleClose={() => setIsOpenRecordPaymentModal(false)}
            isOpen={isOpenRecordPaymentModal}
            title={I18n.record_payment}
            selectedInvoice={transaction}
            isLoading={isLoadingRecordPayment}
          />
        </Box>
      ) : null}
    </Box>
  );
};

const enhance = withObservables(["customer"], ({ transaction }) => ({
  customerDetail: transaction?.customer
    ?.observe()
    .pipe(catchError((error) => of({}))),
  orders: transaction.orders.observe(),
}));

export default enhance(InvoiceRowItem);
